import React, { useState, useEffect } from "react";
import { BaseDialog } from "../../shared/BaseDialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Button } from "@shared/ui/button";
import { FormService, FormField } from "@shared/services/FormServices";
import { Info } from "lucide-react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { toSnakeCase } from "@shared/utils/caseConverter";

interface UppercaseTransformationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetField: string;
  fileData: string[];
  onApplyTransformation: (config: any) => void;
}

const UppercaseTransformationDialog: React.FC<
  UppercaseTransformationDialogProps
> = ({ isOpen, onClose, targetField, fileData, onApplyTransformation }) => {
  const [formData, setFormData] = useState({
    applyTo: "entire",
    preserveSpacesPunctuation: true,
  });

  const [previewData, setPreviewData] = useState<{
    before: string[];
    after: string[];
  }>({
    before: [],
    after: [],
  });

  useEffect(() => {
    if (fileData) {
      const samples = fileData.filter((s) => s && s !== "...").slice(0, 2);
      setPreviewData({ before: samples, after: [] });
      updatePreview(
        { applyTo: "entire", preserveSpacesPunctuation: true },
        samples
      );
    }
  }, [fileData]);

  const fields: FormField[] = [
    {
      name: "applyTo",
      type: "radio",
      label: "Apply to",
      options: [
        { value: "entire", label: "Entire text" },
        { value: "first_letter", label: "First letter only" }, // Changed from "first-only"
        { value: "first_letter_each_word", label: "First letter of each word" }, // Changed from "first-each"
      ],
      defaultValue: "entire",
      className: "col-span-1",
    },
    {
      name: "preserveSpacesPunctuation",
      type: "checkbox",
      label: "Preserve spaces/punctuation",
      defaultValue: true,
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields,
    mode: "onChange",
    defaultValues: formData,
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    const newFormData = { ...formData, [fieldName]: value };
    setFormData(newFormData);
    updatePreview(newFormData, previewData.before);
  };

  const updatePreview = (data: any, samples: string[]) => {
    const results = samples.map((item) => {
      switch (data.applyTo) {
        case "entire":
          return item.toUpperCase();
        case "first_letter": // Changed from "first_only"
          return item.length > 0
            ? item.charAt(0).toUpperCase() + item.slice(1)
            : "";
        case "first_letter_each_word": // Changed from "first_each"
          // Match backend: split by space and capitalize first letter of each word
          return item
            .split(" ")
            .map((word) =>
              word.length > 0 ? word[0].toUpperCase() + word.slice(1) : ""
            )
            .join(" ");
        default:
          return item;
      }
    });
    setPreviewData({ before: samples, after: results });
  };

  const handleApply = () => {
    onApplyTransformation({
      targetField: toSnakeCase(targetField),
      transformation: {
        type: "uppercase",
        order: 1,
        config: { apply_to: formData.applyTo },
      },
    });
    onClose();
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Uppercase Configuration"
      className="max-w-4xl w-full max-h-[90vh] flex flex-col"
    >
      <div className="space-y-4 overflow-y-auto flex-1 px-6 py-4">
        {/* Info */}
        <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 p-4 rounded-md">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Convert to Uppercase:</strong> Standardize text formatting
            for codes, abbreviations, or consistent data entry.
          </div>
        </div>

        {/* Form */}
        <DynamicForm
          fields={fields}
          form={form}
          config={{
            fields,
            layout: "vertical",
            showSubmitButton: false,
            showResetButton: false,
            classes: FIELD_CLASSES,
          }}
          onFieldChange={handleFieldChange}
          gridColumns={1}
        />

        {/* Preview */}
        <div className="border rounded-lg p-4 bg-gray-50 relative">
          <h4 className="font-medium mb-3 text-gray-900">Preview</h4>
          <div className="grid grid-cols-2 gap-6 relative">
            {/* Before */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Before</p>
              <div className="space-y-2">
                {previewData.before.length > 0 ? (
                  previewData.before.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-3 rounded-md border border-gray-200"
                    >
                      <span className="text-sm text-gray-800">{item}</span>
                    </div>
                  ))
                ) : (
                  <div className="bg-white p-3 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-400">
                      No data available
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Arrow */}
            <div className="flex items-center justify-center absolute left-1/2 transform -translate-x-1/2 top-1/2 -translate-y-1/2">
              <span className="text-gray-400 text-lg">→</span>
            </div>

            {/* After */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">After</p>
              <div className="space-y-2">
                {previewData.after.length > 0 ? (
                  previewData.after.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-3 rounded-md border border-gray-200"
                    >
                      <span className="text-sm text-gray-800">{item}</span>
                    </div>
                  ))
                ) : (
                  <div className="bg-white p-3 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-400">
                      No preview available
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply}>Apply Transformation</Button>
        </div>
      </div>
    </BaseDialog>
  );
};

export default UppercaseTransformationDialog;
